import { useEffect, useState } from "react";
import { Loader, Dimmer, List, Label, Icon, Button, Segment, Header } from "semantic-ui-react";
import type { SemanticCOLORS } from "semantic-ui-react";

import { useAuthContext } from "../../../contexts/AuthContext";
import FHIRDelete from "../../../data/FHIRDelete";
import type { FlatFlag } from '../../../types/flat-fhir';
import * as FHIRUtils from "../../../utils/FHIRUtils"
import type { ResourceEntry } from '../../../utils/bundleNarrowing';

import NewFlag from "./newFlag";
import type { FlagEntry, FlagsListProps } from './types';



function FlagsList(props: FlagsListProps) {

  const authInfo = useAuthContext();
  const [dimmerProcess, setDimmerProcess] = useState(false);
  const [flagsList, setFlagsList] = useState<FlagEntry[] | undefined>(props.flagsList);
  const [_open, setOpen] = useState(false);
  const labelColors: Record<string, string> = {
    'REDFLAG': 'red',
    'INFO': 'teal',
    'WARNING': 'orange'
  }

  const [deleteConfirm, setDeleteConfirm] = useState(false);

  /*
   * `useState(null)` inferred `null`, so `setSelectedFlag(parsedFlag)` was
   * assigning a `FlatFlag` into a slot the compiler believed could only ever be
   * `null` — and the reads below (`selectedFlag.code`, `selectedFlag.text`)
   * were reads off `null`. Naming the union states what actually flows: `null`
   * until a delete is armed, a `FlatFlag` after `handleDeleteConfirm`.
   *
   * The `null` arm is real and reachable, which is why the reads are asserted
   * rather than the type narrowed to `FlatFlag`; see the SUSPECTED DEFECT at
   * the delete-confirm block.
   */
  const [selectedFlag, setSelectedFlag] = useState<FlatFlag | null>(null);

  useEffect(() => {
    setFlagsList(props.flagsList);
  }, [props.flagsList])


  const handleDeleteConfirm = (parsedFlag: FlatFlag) => {
    setSelectedFlag(parsedFlag);
    setDeleteConfirm(true);
  }

  const deleteFlag = (parsedFlag: FlatFlag) => {
    setDimmerProcess(true);

    FHIRDelete("/Flag/" + parsedFlag.id, authInfo.token).then(_response => {
      // `!` not `?.`: the producer always supplies `refreshFlags`, and if one
      // ever did not, calling it throws today. The assertion erases; optional
      // chaining would emit a guard and silently skip the refetch, leaving a
      // deleted flag on screen.
      props.refreshFlags!();
      setDimmerProcess(false);
      setDeleteConfirm(false);
    }, _error => {
      setDimmerProcess(false);
      setDeleteConfirm(false);
    })
  }


  return (
    <div>
      {flagsList && !props.flagError &&
        <div>
          <Dimmer active={dimmerProcess} inverted>
            <Loader />
          </Dimmer>
          {
            !deleteConfirm &&
            <>
              {flagsList && flagsList.length !== 0 && (<List style={{ padding: "10px" }} horizontal >
                {flagsList.map((flag: ResourceEntry<'Flag'>) => {
                  const parsedFlag = FHIRUtils.parseFHIRFlags(flag.resource)
                  return (
                    !parsedFlag.assignedTo && (<List.Item key={parsedFlag.id} className="cb_margin_0_3">
                      
                      {/*
                        `labelColors` is keyed by the three flag codes the app
                        creates; a Flag carrying any other code yields
                        `undefined`, which Semantic renders as an uncoloured
                        label. Asserted to `SemanticCOLORS` to record that pass
                        — supplying a default colour would change what a
                        stranger-coded flag looks like.
                      */}
                      <Label className="cb_margin_left_0" color={labelColors[parsedFlag.code] as SemanticCOLORS}>{parsedFlag.text}<Icon onClick={(_e: React.SyntheticEvent<HTMLElement>) => handleDeleteConfirm(parsedFlag)} name='delete' /></Label>
                      
                      
                    </List.Item>
                    ))
                })}

              </List>)
              }
              {/*
                SUSPECTED DEFECT — `props.token` is passed here but no producer
                supplies it. `banner/index.tsx` renders `FlagsList` at both of
                its call sites with `patientId`, `flagsList`, `org`,
                `refreshFlags` and `flagError` (plus `setBannerColor` at the
                second) — never `token`. So this attribute is always
                `undefined`.

                Inert today: `NewFlag` does not read `props.token` either. It
                takes the auth token from `useAuthContext()` directly, which is
                where the real one comes from. The prop is a leftover from
                before that context existed.

                Left in place. Removing it is dead-prop cleanup rather than a
                type statement, and it changes the props object React receives.
              */}
              <NewFlag token={props.token} org={props.org} setOpen={setOpen} patientId={props.patientId} refreshFlags={props.refreshFlags} />
            </>
          }

          {/*
            The `selectedFlag` reads here are asserted non-null because the
            invariant that makes them safe lives in a SEPARATE piece of state:
            `handleDeleteConfirm` calls `setSelectedFlag(parsedFlag)` and
            `setDeleteConfirm(true)` together, so `deleteConfirm` is only ever
            true while `selectedFlag` holds a flag. Nothing expresses that
            pairing — two independent `useState`s encode one two-state machine —
            so the compiler cannot see it and neither can a reader who changes
            one setter without the other.

            Asserted rather than guarded: a `selectedFlag &&` guard would render
            nothing where the confirm panel renders today if the pairing were
            ever broken, which is a different failure than the current throw.
          */}
          {deleteConfirm &&
            <Segment vertical >
              <Header>
                <span style={{ marginLeft: "5px", fontSize:"13px" }}>Are you sure you want to delete? </span>
                <Label className="cb_margin_left_0" color={labelColors[selectedFlag!.code] as SemanticCOLORS}>{selectedFlag!.text}</Label>
              </Header>
              <Segment.Inline style={{ textAlign: "center" }}>
                <Button.Group compact size="mini">
                  <Button primary name='yes' onClick={() => deleteFlag(selectedFlag!)}>Yes</Button>
                  <Button.Or text='or' />
                  <Button primary name='no' onClick={() => setDeleteConfirm(false)}>No</Button>
                </Button.Group>
              </Segment.Inline>
            </Segment>

          }
        </div>
      }
    </div>
  );
}

export default FlagsList;